import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";
import api from "@/lib/api";

const INITIAL = {
  name: "", email: "", phone: "",
  animal_type: "", colour: "",
  child_name: "", birthday: "",
  description: "",
  budget: "", deadline: "",
};

export default function CustomOrderPage() {
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
      toast.success("Your personalisation request is in — we'll reply soon");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-16 lg:py-24" data-testid="personalise-page">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-16">
        <div className="relative">
          <div className="font-script text-2xl text-[#D97757]">made just for them</div>
          <h1 className="font-serif text-5xl lg:text-6xl mt-2 leading-tight">
            Personalise your little friend.
          </h1>
          <p className="mt-6 text-[#5C4D43] leading-relaxed">
            Pick your animal, choose the colour, and add the name and birthday of the little one
            it&apos;s for — we&apos;ll stitch the rest. Every personalised friend is one-of-a-kind
            and made just for you.
          </p>

          <div className="mt-10 space-y-4">
            <Step n="01" text="Tell us the animal, colour and any little details." />
            <Step n="02" text="Add the name and birthday for the heart tag." />
            <Step n="03" text="We reply with a sketch, timeline & quote within 48 hours." />
            <Step n="04" text="You approve, we crochet, your friend ships UK Royal Mail 2nd class." />
          </div>

          <div className="hidden lg:flex mt-16 p-6 bg-[#F4EFE6] rounded-3xl items-start gap-3">
            <Sparkles className="text-[#D97757] shrink-0" />
            <div className="text-sm text-[#5C4D43]">
              Loved as <span className="font-medium text-[#2C211B]">baby shower, 1st birthday and adoption gifts.</span>
            </div>
          </div>
        </div>

        <div className="card-soft p-8 lg:p-10" data-testid="personalise-form-card">
          {submitted ? (
            <div className="text-center py-12" data-testid="personalise-form-success">
              <div className="font-serif text-3xl">Thank you!</div>
              <p className="mt-3 text-[#5C4D43]">
                Your personalisation request is in our basket. We&apos;ll reply within 48 hours.
              </p>
              <button
                data-testid="personalise-form-reset"
                onClick={() => { setSubmitted(false); setForm(INITIAL); }}
                className="btn-secondary mt-6"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="text-xs uppercase tracking-[0.18em] text-[#5C4D43]">About you</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input data-testid="inq-name" required className="input-warm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
                <Field label="Email" required>
                  <input data-testid="inq-email" required type="email" className="input-warm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
              </div>
              <Field label="Phone (optional)">
                <input data-testid="inq-phone" className="input-warm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>

              <div className="pt-3 text-xs uppercase tracking-[0.18em] text-[#5C4D43]">The animal</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Type of animal" required>
                  <input data-testid="inq-animal" required className="input-warm" placeholder="e.g. fox, sheep, dinosaur" value={form.animal_type} onChange={(e) => setForm({ ...form, animal_type: e.target.value })} />
                </Field>
                <Field label="Preferred colour">
                  <input data-testid="inq-colour" className="input-warm" placeholder="e.g. cream & sage" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} />
                </Field>
              </div>

              <div className="pt-3 text-xs uppercase tracking-[0.18em] text-[#5C4D43]">For the heart tag</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Child's name (optional)">
                  <input data-testid="inq-child-name" className="input-warm" placeholder="e.g. Olive" value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} />
                </Field>
                <Field label="Birthday (optional)">
                  <input data-testid="inq-birthday" type="date" className="input-warm" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
                </Field>
              </div>

              <Field label="Anything else?" required>
                <textarea
                  data-testid="inq-description"
                  required
                  className="input-warm min-h-[120px]"
                  placeholder="Outfit, accessories, mood, recipient, special message — anything else we should know."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Budget (optional)">
                  <input data-testid="inq-budget" className="input-warm" placeholder="£35 – £70" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
                </Field>
                <Field label="Needed by (optional)">
                  <input data-testid="inq-deadline" type="date" className="input-warm" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
                </Field>
              </div>

              <button
                data-testid="inq-submit"
                disabled={busy}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send size={16} /> {busy ? "Sending..." : "Send my personalisation request"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-4">
      <div className="font-serif text-2xl text-[#D97757] leading-none">{n}</div>
      <div className="text-[#2C211B]">{text}</div>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm text-[#5C4D43] mb-2 block">{label}{required && <span className="text-[#D97757]"> *</span>}</span>
      {children}
    </label>
  );
}
